import { video } from '@wordpress/icons';
import { useDispatch } from '@wordpress/data';
import { store as coreStore } from '@wordpress/core-data';
import { __, sprintf, _n } from '@wordpress/i18n';
import { store as noticesStore } from '@wordpress/notices';
import { useState } from '@wordpress/element';
import {
	Button,
	__experimentalText as Text,
	__experimentalHStack as HStack,
	__experimentalVStack as VStack,
} from '@wordpress/components';
import { getItemTitle } from './utils';

export const StartStopButton = ( props ) => {
	const { closeModal, items, onActionPerformed, start, disabled } = props;

	const [ isBusy, setIsBusy ] = useState( false );
	const { createSuccessNotice, createErrorNotice } =
		useDispatch( noticesStore );
	const { saveEntityRecord } = useDispatch( coreStore );

	return (
		<Button
			__next40pxDefaultSize
			{ ...props }
			onClick={ async ( e ) => {
				e.stopPropagation();
				setIsBusy( true );
				const promiseResult = await Promise.allSettled(
					items.map( ( item ) =>
						saveEntityRecord(
							'postType',
							item.type,
							{
								...item,
								meta: {
									popper_rules: {
										...item.meta.popper_rules,
										running:
											items.length === 1
												? ! item.meta.popper_rules
														.running
												: start,
									},
								},
							},
							{ throwOnError: true }
						)
					)
				);
				// If all the promises were fulfilled with success.
				if (
					promiseResult.every(
						( { status } ) => status === 'fulfilled'
					)
				) {
					let successMessage;
					if ( promiseResult.length === 1 ) {
						successMessage = sprintf(
							/* translators: The item's title. */
							__( '"%1$s" %2$s.' ),
							getItemTitle( items[ 0 ] ),
							start ? 'started' : 'stopped'
						);
					} else {
						successMessage = sprintf(
							'%1$s popups %2$s.',
							items.length,
							start ? 'started' : 'stopped'
						);
					}
					createSuccessNotice( successMessage, {
						type: 'snackbar',
						id: 'move-to-trash-action',
					} );
				} else {
					// If there was at least one failure.
					let errorMessage;
					// If we were trying to delete a single item.
					if ( promiseResult.length === 1 ) {
						const typedError = promiseResult[ 0 ];
						if ( typedError.reason?.message ) {
							errorMessage = typedError.reason.message;
						} else {
							errorMessage = __(
								'An error occurred while editing the item.'
							);
						}
						// If we were trying to delete multiple items.
					} else {
						const errorMessages = new Set();
						const failedPromises = promiseResult.filter(
							( { status } ) => status === 'rejected'
						);
						for ( const failedPromise of failedPromises ) {
							if ( failedPromise.reason?.message ) {
								errorMessages.add(
									failedPromise.reason.message
								);
							}
						}
						if ( errorMessages.size === 0 ) {
							errorMessage = __(
								'An error occurred while editing the items.'
							);
						} else if ( errorMessages.size === 1 ) {
							errorMessage = sprintf(
								/* translators: %s: an error message */
								__(
									'An error occurred while editing the item: %s'
								),
								[ ...errorMessages ][ 0 ]
							);
						} else {
							errorMessage = sprintf(
								/* translators: %s: a list of comma separated error messages */
								__(
									'Some errors occurred while editing the items: %s'
								),
								[ ...errorMessages ].join( ',' )
							);
						}
					}
					createErrorNotice( errorMessage, {
						type: 'snackbar',
					} );
				}
				if ( onActionPerformed ) {
					onActionPerformed( items );
				}
				setIsBusy( false );
				closeModal?.();
			} }
			isBusy={ isBusy }
			disabled={ isBusy || disabled }
			iconSize="15"
			accessibleWhenDisabled
		/>
	);
};

const startStop = {
	id: 'start-stop',
	label: __( 'Start/Stop', 'popper' ),
	isPrimary: false,
	icon: video,
	isEligible( item ) {
		return item.status !== 'trash';
	},
	supportsBulk: true,
	hideModalHeader: true,
	RenderModal: ( { items, closeModal, onActionPerformed } ) => {
		return (
			<VStack spacing="5">
				<Text>
					{ items.length === 1
						? sprintf(
								// translators: %"$s: The item's title.
								__(
									'Are you sure you want to %1$s popup "%2$s"?'
								),
								getItemTitle( items[ 0 ] ),
								items[ 0 ].meta.popper_rules.running
									? __( 'Stop' )
									: __( 'Start' )
						  )
						: sprintf(
								// translators: %"$s: The item's title.
								_n(
									'Do you want start or stop %d popup?',
									'Do you want start or stop %d popups?',
									items.length
								),
								items.length
						  ) }
				</Text>
				<HStack justify="center">
					<StartStopButton
						closeModal={ closeModal }
						items={ items }
						onActionPerformed={ onActionPerformed }
						start={ false }
						text={ __( 'Stop' ) }
						icon="controls-pause"
						disabled={
							items.length === 1
								? ! items[ 0 ].meta.popper_rules.running
								: false
						}
						variant="secondary"
					/>
					<StartStopButton
						closeModal={ closeModal }
						items={ items }
						onActionPerformed={ onActionPerformed }
						start={ true }
						text={ __( 'Start' ) }
						icon="controls-play"
						disabled={
							items.length === 1
								? items[ 0 ].meta.popper_rules.running
								: false
						}
						variant="primary"
					/>
				</HStack>
			</VStack>
		);
	},
};

export default startStop;
